import type { MetricValues, SecondaryDimensionValues } from '@revelio/core';

export const MAX_REPORT_ENTITIES = 10;
export const MAX_REPORT_COMPETITORS = MAX_REPORT_ENTITIES - 1;

export const DEFAULT_GRID_ROWS = 36;
export const DEFAULT_GRID_COLUMNS = 48;
export const REPORT_PAGE_HEIGHT = 1100;
export const REPORT_PAGE_WIDTH = 850;
export const REPORT_PAGE_PADDING_TOP_ROWS = 2;
export const REPORT_PAGE_PADDING_BOTTOM_ROWS = 2;

export const REPORT_THEME = {
  page: {
    height: REPORT_PAGE_HEIGHT,
    width: REPORT_PAGE_WIDTH,
  },

  margins: {
    pdf: 42,
    minCenterMargin: 42, // Minimum margin for centering (max(calc(50% - halfWidth), 42px))
  },

  grid: {
    maxSafeRows: 72,
  },
} as const;

export const metricToTitleMap: Record<MetricValues, string> = {
  attrition_rate: 'Attrition Rate',
  growth_rate: 'Growth Rate',
  hiring_rate: 'Hiring Rate',
  headcount: 'Headcount',
  salary: 'Salary',
  tenure: 'Tenure',
  transitions_inflow: 'Transitions Inflow',
  transitions_outflow: 'Transitions Outflow',
  transitions_inflows_and_outflows: 'Transitions Inflow / Outflow',
  screener_headcount: 'Screener Headcount',
  screener_growth: 'Screener Growth',
  screener_growth_yoy: 'Screener Growth YoY',
  screener_hiring: 'Screener Hiring',
  screener_attrition: 'Screener Attrition',
  screener_salary: 'Screener Salary',
  screener_tenure: 'Screener Tenure',
  screener_inflow: 'Screener Inflow',
  screener_outflow: 'Screener Outflow',
  posting_salary: 'Posting Salary',
  time_to_fill: 'Time to Fill',
  postings_active: 'Postings Active',
  postings_new: 'Postings New',
  postings_removed: 'Postings Removed',
  postings_volume: 'Postings Volume',
  base_salary: 'Base Salary',
  total_compensation: 'Total Compensation',
  salary_growth: 'Salary Growth (YoY)',
  total_spend_salary: 'Total Spend (Salary)',
  total_spend_compensation: 'Total Spend (Compensation)',
  contractor_pay_rate: 'Hourly Rate',
  base_salary_percentiles: 'Base Salary Percentiles',
  total_compensation_percentiles: 'Total Compensation Percentiles',
  contractor_pay_rate_percentiles: 'Hourly Rate Percentiles',
  td_users: 'TD Users',
  entity_summary: 'Entity Summary',
  demand_supply: 'Demand/Supply',
  demand_supply_percentile: 'Demand/Supply Percentile',
  wage_pressure: 'Wage Pressure',
  wage_pressure_percentile: 'Wage Pressure Percentile',
  market_tightness: 'Market Tightness',
  market_share: 'Market Share',
  target_companies_rec: 'Target Companies',
  time_to_fill_percentile: 'Time to Fill Percentile',
  overview_map: 'Overview Map',
  positive_review: 'Positive Review',
  negative_review: 'Negative Review',
  overall: 'Overall',
  sentiment_top_bottom: 'Sentiment Drivers',
  business_outlook: 'Business Outlook',
  career_growth: 'Career Growth',
  compensation: 'Compensation',
  benefits: 'Benefits',
  healthcare: 'Healthcare',
  retirement_pension: 'Retirement Pension',
  childcare: 'Childcare',
  education_assistance: 'Education Assistance',
  wellness_programs: 'Wellness Programs',
  work_life_balance: 'Work-Life Balance',
  diversity_and_inclusion: 'Diversity & Inclusion',
  leadership: 'Leadership',
  culture: 'Culture',
  clients_and_customers: 'Clients and Customers',
  company_growth: 'Company Growth',
  schedule_flexibility: 'Schedule Flexibility',
  job_security: 'Job Security',
  overtime: 'Overtime',
  family_support: 'Family Support',
  commission: 'Commission',
  independence: 'Independence',
  commute: 'Commute',
  safety: 'Safety',
  internal_communication: 'Internal Communication',
  office_politics: 'Office Politics',
  harassment: 'Harassment',
  honesty: 'Honesty',
  ethics: 'Ethics',
  commitment: 'Commitment',
  compliance: 'Compliance',
  integrity: 'Integrity',
  coworkers: 'Coworkers',
  mission_and_vision: 'Mission and Vision',
  workplace_facilities: 'Workplace Facilities',
  training: 'Training',
  efficiency_and_bureaucracy: 'Efficiency and Bureaucracy',
  product_and_service_quality: 'Product and Service Quality',
  prestige: 'Prestige',
  remote_work: 'Remote Work',
  onboarding: 'Onboarding',
  mentorship: 'Mentorship',
  technology: 'Technology',
  promotion_opportunities: 'Promotion Opportunities',
  paid_time_off: 'Paid Time Off',
  transparency: 'Transparency',
  share_of_headcount: 'Share of Headcount',
};

export const secondaryDimensionToTitleMap: Record<
  SecondaryDimensionValues,
  string
> = {
  rics: 'Industry',
  geography: 'Geography',
  role: 'Role',
  company: 'Company',
  skills: 'Skills',
  gender: 'Gender',
  ethnicity: 'Ethnicity',
  education: 'Education',
  seniority: 'Seniority',
  timeseries: 'Time',
  competitors: 'Competitors',
};

/** Labels for "Top {{segment}}" chart titles (e.g. "Industries", "Companies"). */
export const segmentLabelForChartTitle: Record<
  SecondaryDimensionValues,
  string
> = {
  rics: 'Industries',
  geography: 'Geographies',
  role: 'Roles',
  company: 'Companies',
  skills: 'Skills',
  gender: 'Genders',
  ethnicity: 'Ethnicities',
  education: 'Educations',
  seniority: 'Seniorities',
  timeseries: 'Timeseries',
  competitors: 'Competitors',
};
